import React, { useState } from 'react';

// Parse markdown-style links and auto-detect URLs in content
function parseContent(content) {
  // Combined regex to match both markdown links and raw URLs
  const combinedRegex = /(\[([^\]]+)\]\(([^)]+)\))|(https?:\/\/[^\s<>[\]()]+|www\.[^\s<>[\]()]+)/gi;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = combinedRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: content.substring(lastIndex, match.index) });
    }
    
    if (match[1]) {
      // Markdown-style link: [text](url)
      parts.push({ type: 'link', text: match[2], url: match[3] });
    } else if (match[4]) {
      // Raw URL
      let url = match[4];
      const href = url.startsWith('www.') ? `https://${url}` : url;
      parts.push({ type: 'link', text: url, url: href });
    }
    
    lastIndex = match.index + match[0].length;
  }
  
  if (lastIndex < content.length) {
    parts.push({ type: 'text', content: content.substring(lastIndex) });
  }

  return parts.length > 0 ? parts : [{ type: 'text', content }];
}

// Render content with parsed links and markdown-style bold
function RenderContent({ content, className = '' }) {
  const parts = parseContent(content);
  
  return (
    <span className={className}>
      {parts.map((part, index) => {
        if (part.type === 'link') {
          return (
            <a
              key={index}
              href={part.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 hover:text-indigo-800 underline"
            >
              {part.text}
            </a>
          );
        }
        const boldParts = part.content.split(/\*\*([^*]+)\*\*/g);
        return boldParts.map((text, i) => {
          if (i % 2 === 1) {
            return <strong key={`${index}-${i}`} className="font-semibold">{text}</strong>;
          }
          return <span key={`${index}-${i}`}>{text}</span>;
        });
      })}
    </span>
  );
}

function formatDateTime(dateString) {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Admin email check - matches logic in ConversationTimeline
const ADMIN_EMAIL_DOMAINS = ['zapt.ai'];
function isAdminEmail(email) {
  if (!email) return false;
  return ADMIN_EMAIL_DOMAINS.some(domain => email.endsWith(`@${domain}`));
}

// Get what the user sees for a given author email
function getUserView(authorEmail, appOwnerEmail) {
  if (!authorEmail) return 'User';
  if (authorEmail === appOwnerEmail) return 'You';
  if (isAdminEmail(authorEmail)) return 'ZAPT Team';
  return authorEmail.split('@')[0];
}

// Get note type badge/styling
function getNoteTypeBadge(noteType) {
  if (noteType === 'feature_request') {
    return {
      badge: (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
          💡 Feature Request
        </span>
      ),
      bgClass: 'bg-amber-50 border-amber-200',
    };
  }
  if (noteType === 'system') {
    return {
      badge: (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
          📦 System
        </span>
      ),
      bgClass: 'bg-blue-50 border-blue-200',
    };
  }
  return {
    badge: null,
    bgClass: 'bg-gray-50 border-gray-200',
  };
}

export default function AdminNotesSection({ app, appId, onAddNote, addingNote }) {
  const appData = app;
  
  const [newNoteContent, setNewNoteContent] = useState('');

  const handleAddNote = async () => {
    if (!newNoteContent.trim()) return;
    await onAddNote(newNoteContent.trim());
    setNewNoteContent('');
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">💬 Notes & Messages</h2>
      
      {/* Notes Thread */}
      {appData?.notes && appData.notes.length > 0 ? (
        <div className="space-y-4 mb-6 pb-6 border-b border-gray-200 max-h-96 overflow-y-auto">
          {appData.notes.map((note) => {
            const { badge, bgClass } = getNoteTypeBadge(note.noteType);
            const isFeatureRequest = note.noteType === 'feature_request';
            
            return (
              <div key={note.id} className={`border rounded-lg p-4 ${bgClass}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-sm font-semibold text-gray-900">
                        {note.authorEmail || 'Unknown'}
                      </span>
                      <span className="text-xs text-gray-400 italic">
                        (User sees: "{getUserView(note.authorEmail, appData?.userEmail)}")
                      </span>
                      {badge}
                      <span className="text-xs text-gray-500">
                        {formatDateTime(note.createdAt)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      <RenderContent content={note.content} />
                    </p>
                  </div>
                </div>
                
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500 mb-6 pb-6 border-b border-gray-200">
          <p className="text-sm">No notes yet. Add a note below to start the conversation.</p>
        </div>
      )}

      {/* Add Note Form */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Add a note (will email the customer):
        </label>
        <textarea
          value={newNoteContent}
          onChange={(e) => setNewNoteContent(e.target.value)}
          rows={4}
          placeholder="Type your message here... This will be sent to the customer via email."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm mb-3"
        />
        <button
          onClick={handleAddNote}
          disabled={addingNote || !newNoteContent.trim()}
          className="px-4 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90 font-medium disabled:opacity-50 disabled:cursor-not-allowed text-sm"
        >
          {addingNote ? 'Adding...' : '💬 Add Note & Send Email'}
        </button>
      </div>
    </div>
  );
}
